import { isArray, sortBy } from "lodash-es";
import { nextTick } from "vue";

import { CancelError } from "@/error/CancelError";
import { addBreadcrumb } from "@/error/sentry";
import { relativeFromViewport } from "@/math/coordinate-utils";
import { distance2 } from "@/math/coordinates";
import { updateTimeout } from "@/model/Settings";
import type { IdMap } from "@/model/baseTypes";
import type { Board } from "@/model/board";
import { useBoardsStore } from "@/store/boards";
import { useCardStore } from "@/store/card";
import { useWorkModeStore } from "@/store/workMode";
import { centerCoord } from "@/utils/dom/coordinates";
import { isBrowserTabActive, untilBrowserTabVisible } from "@/utils/dom/dom";
import { disconnectObservers, reconnectObservers } from "@/utils/dom/mutationObserver";
import { performanceLog } from "@/utils/log";
import { nextTicks } from "@/utils/vue";

import type {
  ServerStickiesUpdate,
  ServerStickiesUpdateCacheHit,
  ServerStickiesUpdateCacheMiss,
  ServerSticky,
} from "../serverModel";
import type { BackendReadSession } from "./BackendReadSession";
import { addSticky, changeSticky, serverStickyPos } from "./changeSticky";

type ServerStickyId = string;

interface ParsedServerStickyUpdates {
  time: number;
  changes: ServerSticky[];
  removes?: ServerStickyId[];
}

export async function loadStickies(backendSess: BackendReadSession, board: Board): Promise<void> {
  let loadStart: number;

  const time = Date.now();
  // Only fetch status distribution if in execution mode
  // (reduces server load, since 90% of the time, users won't need it)
  const fetchStatusDistribution = useWorkModeStore().isExecutionMode;
  // If a card is missing the status distribution, try a full load instead of fetching deltas
  // because the distribution is not published when the graph is built or recovers, so it
  // won't be seen in the updates.
  const hasMissingDistribution = Object.values(board.cards).some(
    (card) =>
      card.data.statusDistributionState === "pending" ||
      card.data.statusDistributionState === "unavailable",
  );
  const since = fetchStatusDistribution && hasMissingDistribution ? 0 : board.state.loadedAt;

  const stickiesOrUpdates = await backendSess.getBoardStickies(
    board.id,
    since,
    fetchStatusDistribution,
  );
  // user logged out / switched session while the stickies where loaded
  if (!useBoardsStore().boards[board.id]) {
    throw new CancelError("Sticky loading cancelled");
  }
  const stickies = parseStickies(stickiesOrUpdates);
  if (fetchStatusDistribution && !isCacheHitServerStickiesUpdate(stickiesOrUpdates)) {
    // A full read with distributions is authoritative: a sticky without a state
    // gets no status distribution.
    stickies.changes.forEach((sticky) => (sticky.status_distribution_state ??= null));
  }

  board.state.loadedAt = stickies.time;
  board.state.hasStickyStatusDistribution = fetchStatusDistribution;

  // If execution mode was enabled while we were loading without distribution,
  // reset loadedAt so the pending re-fetch (triggered by the watcher) gets a full load
  if (!fetchStatusDistribution && useWorkModeStore().isExecutionMode) {
    board.state.loadedAt = 0;
  }

  return new Promise((resolve) => {
    const updated = updateStickies(stickies.changes, resolve);
    deleteStickies(updated, stickies.removes);
  });

  function parseStickies(
    serverStickies: ServerSticky[] | ServerStickiesUpdate,
  ): ParsedServerStickyUpdates {
    if (isCacheMissServerStickiesUpdate(serverStickies)) {
      const { stickies } = serverStickies;
      const stickyIds = new Set(stickies.map((sticky: ServerSticky) => sticky.id));
      const removes: ServerStickyId[] = Object.keys(board.cards).filter(
        (cardId) => !stickyIds.has(cardId),
      );

      return { time: serverStickies.timestamp, changes: stickies, removes };
    }

    if (isCacheHitServerStickiesUpdate(serverStickies)) {
      const { updates } = serverStickies;
      const changes = updates
        .filter((update) => update.event !== "delete_sticky")
        .map((update) => update.obj)
        // For a card we already show, the partial is exactly what changeSticky
        // applies. A new card needs enough state to render: the add event alone
        // caches only the id (the following alter merges in the full state), so
        // an id-only obj is skipped here — like the legacy loader did — and the
        // realtime created event renders it instead.
        .filter((obj) => board.cards[obj.id] || (obj.board_id && obj.type_id))
        .map((obj) => obj as ServerSticky);
      const removes: ServerStickyId[] = updates
        .filter((update) => update.event === "delete_sticky")
        .map((update) => update.obj.id);

      return { time: serverStickies.timestamp, changes, removes };
    }

    return { time, changes: serverStickies };
  }

  function updateStickies(changes: ServerSticky[], onFinish: () => void): IdMap<boolean> {
    const ids: IdMap<boolean> = {};
    const center = relativeFromViewport(centerCoord());
    const sorted = sortBy(changes, (serverSticky) => {
      const pos = serverStickyPos(serverSticky);
      return pos ? distance2(pos, center) : 2;
    });
    const operations = sorted.map((serverSticky: ServerSticky) => {
      ids[serverSticky.id] = true;
      return useCardStore().cards[serverSticky.id]
        ? () => {
            useCardStore().readOnly({ id: serverSticky.id, readOnly: false });
            changeSticky(board, serverSticky.id, serverSticky);
          }
        : () => addSticky(serverSticky);
    });

    addBreadcrumb("render", {
      message: "Updating stickies on the board",
      data: {
        count: operations.length,
        type: board.type,
        boardId: board.id,
        tabActive: isBrowserTabActive(),
      },
    });
    disconnectObservers();
    loadStart = performance.now();
    setTimeout(
      () =>
        processOperations(operations, 25, board.id, () => {
          performanceLog("Load stickies", loadStart);
          reconnectObservers();
          onFinish();
        }),
      0,
    );
    return ids;
  }

  function processOperations(
    operations: Array<() => void>,
    batchSize: number,
    boardId: Board["id"],
    onFinish: () => void,
  ) {
    addBreadcrumb("render", {
      message: "Render batch",
      data: { count: operations.length, batchSize, boardId, tabActive: isBrowserTabActive() },
    });
    const start = performance.now();
    operations.slice(0, batchSize).forEach((operation) => operation());
    if (operations.length > batchSize) {
      setTimeout(() => {
        void untilBrowserTabVisible().then(() => {
          void nextTick(() => {
            const end = performance.now();
            processOperations(
              operations.slice(batchSize),
              Math.ceil((batchSize * updateTimeout) / (end - start)),
              boardId,
              onFinish,
            );
          });
        });
      }, 1);
    } else {
      // 2 is found by trial: check that the last tick takes (nearly) no time
      nextTicks(2, onFinish);
    }
  }

  function deleteStickies(updateIds: IdMap<boolean>, deleteIds?: string[]) {
    if (deleteIds) {
      deleteIds.forEach((id) => {
        useCardStore().delete({ id, boardId: board.id });
      });
    } else {
      for (const c in board.cards) {
        if (!updateIds[c]) {
          useCardStore().delete({ id: c, boardId: board.id });
        }
      }
    }
  }
}

function isCacheHitServerStickiesUpdate(
  serverStickies: ServerSticky[] | ServerStickiesUpdate,
): serverStickies is ServerStickiesUpdateCacheHit {
  return !isArray(serverStickies) && "updates" in serverStickies;
}

function isCacheMissServerStickiesUpdate(
  serverStickies: ServerSticky[] | ServerStickiesUpdate,
): serverStickies is ServerStickiesUpdateCacheMiss {
  return !isArray(serverStickies) && "stickies" in serverStickies;
}
